import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { TiptapDisplay } from "@msuite/ui/libs/tiptap";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { ServiceFulfillmentTypeTag, UnitTag } from "@msuite/ui/tag";
import { getAtomicNumberString } from "@msuite/utils/organizations";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import Notes from "@/components/notes";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useService } from "@/hooks/use-service";
import type { ServicePanelBaseProps } from "../shared";
import { ServiceHierachyRow } from "./service-hierachy-row";

export function ServiceGeneral({ serviceId }: ServicePanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { service } = useService(serviceId);
	const mentionOptions = useMentionOptions();

	/** Render */
	return (
		<Panels>
			<Panels.Panel>
				{service && (
					<React.Fragment>
						<ContentCard
							icon={icon("Info")}
							title={t("general.information")}
						>
							<DataGrid>
								<DataGrid.Row>
									<DataGrid.Label>{t("organization.data.atomic_number")}</DataGrid.Label>
									<DataGrid.Value>{getAtomicNumberString(service.fullNumber)}</DataGrid.Value>
								</DataGrid.Row>
								<DataGrid.Row>
									<DataGrid.Label>{t("organization.data.fulfillment_type")}</DataGrid.Label>
									<DataGrid.Cell>
										<ServiceFulfillmentTypeTag fulfillmentType={service.fulfillmentType} />
									</DataGrid.Cell>
								</DataGrid.Row>
								<DataGrid.Divider />
								<DataGrid.Row>
									<DataGrid.Label>{t("units.unit")}</DataGrid.Label>
									<DataGrid.Cell>
										<UnitTag unit={service.unit} />
									</DataGrid.Cell>
								</DataGrid.Row>
								{service.parentServiceId && (
									<React.Fragment>
										<DataGrid.Divider />
										<DataGrid.Row>
											<DataGrid.Label>{t("organization.data.hierachy")}</DataGrid.Label>
											<DataGrid.Value />
										</DataGrid.Row>
										<ServiceHierachyRow serviceId={service.parentServiceId} />
									</React.Fragment>
								)}
							</DataGrid>
						</ContentCard>
						<ContentCard
							icon={icon("Text")}
							title={t("general.description")}
						>
							<DataGrid>
								<DataGrid.Row>
									<DataGrid.Label>{t("organization.data.short_text")}</DataGrid.Label>
									<DataGrid.Value>{guardString(service?.shortText)}</DataGrid.Value>
								</DataGrid.Row>
								<DataGrid.Row>
									<DataGrid.Label>{t("organization.data.long_text")}</DataGrid.Label>
									<DataGrid.Cell>
										<TiptapDisplay
											json={guardString(service?.longText)}
											{...mentionOptions}
										/>
									</DataGrid.Cell>
								</DataGrid.Row>
							</DataGrid>
						</ContentCard>
					</React.Fragment>
				)}
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<Notes
					notesTable="organizationServiceNotes"
					identifier={serviceId}
				/>
			</Panels.Panel>
		</Panels>
	);
}
